import React, { forwardRef, useEffect, useRef } from 'react';
import moment from 'moment';
import { styled } from '@mui/material';

import { LineMarker, NoTasks, TimelineTaskCard } from 'components/organisms/Task';
import { useAuth } from 'contexts/auth';
import { Box, Button, ButtonGroup, Container, Icon, IconButton, Stack, TextInput } from 'shell/components';
import { STATUS_VALUES } from 'utils/constants';

const Tablet = forwardRef(
    (
        {
            viewOptions,
            view,
            setView,
            taskList,
            columns = [],
            isSorted,
            numFilters,
            searchBarInput,
            onChangeSearchBarInput,
            openCreateTaskDrawer,
            openFilterDrawer,
            onDragEnd,
            onOpenTask,
            onDeleteTask,
            onDuplicateTask,
            updateTask,
            onMouseEnter,
            onMouseLeave,
        },
        ref
    ) => {
        const { auth } = useAuth();
        const scrollContainerRef = useRef(null);
        const markerRef = useRef(null);

        const tasksGroupedByDueDate = [
            ...taskList.toDo,
            ...taskList.inProgress,
            ...taskList.underReview,
            ...taskList.completed,
        ]
            .filter(({ active }) => active)
            .sort((a, b) => a.end_on - b.end_on)
            .map((task) => ({ ...task, formattedDueDate: moment(task.end_on).format('MMM D YYYY') }))
            .reduce(
                (acc, cur) => ({
                    ...acc,
                    [cur.formattedDueDate]: [...(acc[cur.formattedDueDate] || []), cur],
                }),
                {}
            );

        const taskGroups = Object.values(tasksGroupedByDueDate);
        const formattedToday = moment().format('MMM D YYYY');
        let indexToInsertLine = taskGroups.findIndex((taskGroup) => {
            const { formattedDueDate } = taskGroup[0];
            if (moment(formattedDueDate).valueOf() < moment(formattedToday).valueOf()) return false;
            return true;
        });
        if (indexToInsertLine === -1) indexToInsertLine = taskGroups.length;
        taskGroups.splice(indexToInsertLine, 0, 'line');

        useEffect(() => {
            if (markerRef.current && scrollContainerRef.current) {
                const { x } = markerRef.current.getBoundingClientRect();
                scrollContainerRef.current.scrollLeft = x - 28;
            }
        }, []);

        const PageInner = (
            <RootStack spacing={5}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <ButtonGroup sx={{ width: 250 }} options={viewOptions} selected={view} onChange={setView} />
                    <Stack alignItems={'center'} direction={'row'} spacing={3}>
                        <Button
                            sx={{ flexShrink: 0 }}
                            color={'neutral'}
                            startIcon={<Icon kind={'filter'} />}
                            variant={'tertiary'}
                            onClick={openFilterDrawer}
                        >
                            Filters {numFilters ? `(${numFilters})` : ''}
                        </Button>
                        <TextInput
                            sx={{ maxWidth: 300 }}
                            disablePointerEventsOnIcon={!Boolean(searchBarInput)}
                            endIcon={
                                searchBarInput ? (
                                    <IconButton
                                        aria-label={'clear search input'}
                                        kind={'exit'}
                                        variant={'square'}
                                        onClick={() => onChangeSearchBarInput('')}
                                        onMouseDown={(e) => e.preventDefault()}
                                    />
                                ) : (
                                    <Icon kind={'search'} />
                                )
                            }
                            placeholder={'Search'}
                            value={searchBarInput}
                            onChange={(e) => onChangeSearchBarInput(e.target.value)}
                        />
                        <CustomButton
                            startIcon={<Icon kind={'add'} />}
                            onClick={() => openCreateTaskDrawer('toDo')}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                        >
                            New Task
                        </CustomButton>
                    </Stack>
                </Stack>
                <CustomBox ref={scrollContainerRef}>
                    {taskGroups.length ? (
                        <RowStack direction={'row'} spacing={3}>
                            {taskGroups.map((taskGroup, index) => {
                                return typeof taskGroup === 'string' ? (
                                    <LineMarker ref={markerRef} key={index} />
                                ) : (
                                    <ColumnStack key={index} spacing={3}>
                                        {taskGroup
                                            .sort((a, b) => {
                                                const aStatusValue = STATUS_VALUES[a.status];
                                                const bStatusValue = STATUS_VALUES[b.status];
                                                return aStatusValue - bStatusValue;
                                            })
                                            .map((task) => (
                                                <TimelineTaskCard
                                                    key={task.id}
                                                    task={task}
                                                    onOpenTask={onOpenTask}
                                                    onDeleteTask={onDeleteTask}
                                                    onDuplicateTask={onDuplicateTask}
                                                    updateTask={updateTask}
                                                    onMouseEnter={onMouseEnter}
                                                    onMouseLeave={onMouseLeave}
                                                />
                                            ))}
                                    </ColumnStack>
                                );
                            })}
                        </RowStack>
                    ) : (
                        <NoTasks />
                    )}
                </CustomBox>
            </RootStack>
        );

        return auth.role === 'instructor' ? (
            <Box sx={{ height: 'calc(100vh - 174px)' }} ref={ref}>
                {PageInner}
            </Box>
        ) : (
            <Container ref={ref} device={'tablet'}>
                {PageInner}
            </Container>
        );
    }
);

const RootStack = styled(Stack)(() => {
    return {
        width: '100%',
        height: '100%',
    };
});

const CustomButton = styled(Button)(() => {
    return {
        flexShrink: 0,
    };
});

const CustomBox = styled(Box)(({ theme }) => {
    return {
        border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderBottom: 'none',
        borderRadius: '4px 4px 0 0',
        height: 'calc(100vh - 148px)',
        padding: `0 ${theme.spacing(20)}`,
        background: theme.palette.ensightful.neutral[100],
        overflow: 'auto',
    };
});

const RowStack = styled(Stack)(() => {
    return {
        width: 'max-content',
        minHeight: '100%',
    };
});

const ColumnStack = styled(Stack)(({ theme }) => {
    return {
        zIndex: 2,
        paddingTop: '10vh',
        paddingBottom: theme.spacing(8),
    };
});

export default Tablet;
